import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { PauseCircle, PlayCircle, Power, RefreshCw, AlertTriangle } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  buildQrOrderingNotice,
  getQrOrderingNoticeDisplay,
  parseQrOrderingNotice,
  serializeQrOrderingNotice,
  type QrOrderingDuration,
} from '@/lib/ordering/qrOrderingNotice';
import {
  fetchTenantLocations,
  updateTenantLocation,
  type TenantLocationRecord,
  type UpdateTenantLocationPayload,
} from '@/lib/api/settings';
import { cn } from '@/lib/utils';
import { useTenantStore } from '@/stores/tenantStore';

type OrderingStatus = TenantLocationRecord['qrOrderingStatus'];

const STATUS_BADGE_CLASSES: Record<OrderingStatus, string> = {
  active:
    'border border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300',
  paused:
    'border border-amber-500/40 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300',
  stopped:
    'border border-destructive/50 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/20 dark:text-destructive-foreground',
};

const STATUS_ICON: Record<OrderingStatus, typeof PlayCircle> = {
  active: PlayCircle,
  paused: PauseCircle,
  stopped: Power,
};

const ACTIONS: Array<{
  status: OrderingStatus;
  icon: typeof PlayCircle;
  theme: 'positive' | 'warning' | 'danger';
  requiresDuration?: boolean;
}> = [
  { status: 'active', icon: PlayCircle, theme: 'positive' },
  { status: 'paused', icon: PauseCircle, theme: 'warning', requiresDuration: true },
  { status: 'stopped', icon: Power, theme: 'danger', requiresDuration: true },
];

export interface QrOrderingStatusCardProps {
  className?: string;
}

export const QrOrderingStatusCard: React.FC<QrOrderingStatusCardProps> = ({ className }) => {
  const { t } = useTranslation(['common', 'dashboard']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const activeRestaurantId = useTenantStore((state) => state.activeRestaurantId);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<OrderingStatus | null>(null);
  const [pendingDuration, setPendingDuration] = useState<QrOrderingDuration>('30m');

  const durationOptions = useMemo(
    () => [
      {
        value: '30m' as QrOrderingDuration,
        label: t('dashboard:qrOrdering.duration.options.30m', { defaultValue: '30 minutes' }),
        helper: t('dashboard:qrOrdering.duration.helpers.30m', {
          defaultValue: 'Guests see the warning for the next 30 minutes.',
        }),
      },
      {
        value: '60m' as QrOrderingDuration,
        label: t('dashboard:qrOrdering.duration.options.60m', { defaultValue: '1 hour' }),
        helper: t('dashboard:qrOrdering.duration.helpers.60m', {
          defaultValue: 'Guests see the warning for the next hour.',
        }),
      },
      {
        value: 'manual' as QrOrderingDuration,
        label: t('dashboard:qrOrdering.duration.options.manual', { defaultValue: 'Until I turn it back on' }),
        helper: t('dashboard:qrOrdering.duration.helpers.manual', {
          defaultValue: 'Guests see the warning until you resume QR ordering.',
        }),
      },
    ],
    [t],
  );

  const locationsQueryKey = useMemo(
    () => ['settings', 'locations', activeRestaurantId ?? 'unselected'],
    [activeRestaurantId],
  );

  const locationsQuery = useQuery({
    queryKey: locationsQueryKey,
    queryFn: ({ signal }) => fetchTenantLocations(signal).then((response) => response.locations),
    enabled: Boolean(activeRestaurantId),
  });

  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const isLoading = locationsQuery.isLoading && !locations.length;

  useEffect(() => {
    if (!locations.length) {
      if (selectedLocationId !== null) {
        setSelectedLocationId(null);
      }
      return;
    }

    const hasSelectedLocation =
      typeof selectedLocationId === 'string' && locations.some((location) => location.id === selectedLocationId);

    if (!hasSelectedLocation) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    if (!selectedLocationId) {
      return locations[0] ?? null;
    }
    return locations.find((location) => location.id === selectedLocationId) ?? locations[0] ?? null;
  }, [locations, selectedLocationId]);

  const mutation = useMutation({
    mutationFn: (payload: { locationId: string; status: OrderingStatus; message?: string | null }) => {
      const body: UpdateTenantLocationPayload = { qrOrderingStatus: payload.status };
      if (payload.message !== undefined) {
        body.qrOrderingMessage = payload.message;
      }
      return updateTenantLocation(payload.locationId, body);
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData(locationsQueryKey, (existing: TenantLocationRecord[] | undefined) => {
        if (!existing) {
          return existing;
        }
        return existing.map((location) =>
          location.id === variables.locationId ? response.location : location,
        );
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'locations'] });
      toast({
        title: t('dashboard:qrOrdering.toasts.updated', { defaultValue: 'QR ordering status updated.' }),
      });
      setDialogOpen(false);
      setPendingStatus(null);
    },
    onError: (error: unknown) => {
      console.error('[dashboard] failed to update QR ordering status', error);
      toast({
        variant: 'destructive',
        title: t('dashboard:qrOrdering.toasts.error', { defaultValue: "Couldn't update QR ordering." }),
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const submitStatusChange = (nextStatus: OrderingStatus, duration?: QrOrderingDuration) => {
    if (!selectedLocation) {
      return;
    }
    if (nextStatus !== 'active' && !duration) {
      return;
    }
    let nextMessage: string | null | undefined;
    if (nextStatus === 'active') {
      nextMessage = null;
    } else if (duration) {
      const notice = buildQrOrderingNotice({ status: nextStatus, duration });
      nextMessage = serializeQrOrderingNotice(notice);
    }
    const isSameStatus = nextStatus === selectedLocation.qrOrderingStatus;
    const isSameMessage =
      nextMessage === undefined
        ? true
        : nextMessage === (selectedLocation.qrOrderingMessage ?? null);
    if (isSameStatus && isSameMessage) {
      return;
    }
    mutation.mutate({
      locationId: selectedLocation.id,
      status: nextStatus,
      message: nextMessage,
    });
  };

  const openDurationDialog = (targetStatus: Exclude<OrderingStatus, 'active'>) => {
    if (!selectedLocation || mutation.isPending) {
      return;
    }
    const existingNotice =
      selectedLocation.qrOrderingStatus === targetStatus && selectedLocation.qrOrderingMessage
        ? parseQrOrderingNotice(selectedLocation.qrOrderingMessage)
        : null;
    setPendingDuration(existingNotice?.duration ?? '30m');
    setPendingStatus(targetStatus);
    setDialogOpen(true);
  };

  const handleActionClick = (targetStatus: OrderingStatus, requiresDuration: boolean) => {
    if (!selectedLocation || mutation.isPending) {
      return;
    }
    if (requiresDuration && targetStatus !== 'active') {
      openDurationDialog(targetStatus);
      return;
    }
    submitStatusChange(targetStatus);
  };

  const status = selectedLocation?.qrOrderingStatus ?? 'active';
  const StatusIcon = STATUS_ICON[status];
  const lastUpdatedLabel =
    selectedLocation?.qrOrderingUpdatedAt && Number.isFinite(selectedLocation.qrOrderingUpdatedAt)
      ? formatDistanceToNow(new Date(selectedLocation.qrOrderingUpdatedAt), { addSuffix: true })
      : null;
  const currentNotice = useMemo(() => {
    if (!selectedLocation?.qrOrderingMessage || status === 'active') {
      return null;
    }
    return parseQrOrderingNotice(selectedLocation.qrOrderingMessage);
  }, [selectedLocation?.qrOrderingMessage, status]);
  const currentNoticeDisplay = useMemo(() => {
    if (!currentNotice) {
      return null;
    }
    return getQrOrderingNoticeDisplay(currentNotice);
  }, [currentNotice]);
  const currentGuestCopy =
    status !== 'active'
      ? t(`dashboard:qrOrdering.preview.${status}.${currentNotice?.duration ?? 'manual'}`, {
          defaultValue:
            status === 'paused'
              ? currentNotice?.duration === '30m'
                ? 'Guests see that QR ordering is paused for the next 30 minutes.'
                : currentNotice?.duration === '60m'
                ? 'Guests see that QR ordering is paused for the next hour.'
                : 'Guests see that QR ordering is paused until the team resumes service.'
              : currentNotice?.duration === '30m'
              ? 'Guests see that QR ordering is stopped for the next 30 minutes.'
              : currentNotice?.duration === '60m'
              ? 'Guests see that QR ordering is stopped for the next hour.'
              : 'Guests see that QR ordering is stopped until the team resumes service.',
        })
      : null;
  const resumeTimeLabel =
    currentNoticeDisplay?.resumeAt && currentNoticeDisplay.resumeAt > Date.now()
      ? new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
          new Date(currentNoticeDisplay.resumeAt),
        )
      : null;
  const currentResumeCopy =
    resumeTimeLabel && status !== 'active'
      ? t('dashboard:qrOrdering.preview.resumeAt', {
          defaultValue: 'Guests see this warning until ~{{time}} local time.',
          time: resumeTimeLabel,
        })
      : currentNoticeDisplay?.minutesRemaining && status !== 'active'
      ? t('dashboard:qrOrdering.preview.resumeIn', {
          defaultValue: 'Guests see this warning for ~{{minutes}} min.',
          minutes: currentNoticeDisplay.minutesRemaining,
        })
      : null;

  if (!activeRestaurantId) {
    return null;
  }

  if (!isLoading && locations.length === 0) {
    return null;
  }

  return (
    <>
      <Card className={cn('border border-border bg-card/70 shadow-sm backdrop-blur-sm', className)}>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold">
          {t('dashboard:qrOrdering.title', { defaultValue: 'QR ordering controls' })}
        </CardTitle>
        <CardDescription>
          {t('dashboard:qrOrdering.description', {
            defaultValue: 'Pause QR orders when the line needs breathing room or fully stop scans for closeout.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {locations.length > 1 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('dashboard:qrOrdering.locationLabel', { defaultValue: 'Location' })}
            </p>
            <Select
              value={selectedLocation?.id ?? ''}
              onValueChange={(value) => setSelectedLocationId(value)}
              disabled={isLoading || mutation.isPending}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('dashboard:qrOrdering.locationPlaceholder', {
                    defaultValue: 'Select a location',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background/60 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <Badge className={cn('flex items-center gap-2 px-3 py-1 text-xs font-medium', STATUS_BADGE_CLASSES[status])}>
              <StatusIcon className="h-3.5 w-3.5" />
              {t(`dashboard:qrOrdering.status.${status}`, {
                defaultValue:
                  status === 'active'
                    ? 'Accepting QR orders'
                    : status === 'paused'
                    ? 'Paused'
                    : 'Stopped',
              })}
            </Badge>
            {lastUpdatedLabel ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <RefreshCw className="h-3 w-3" />
                <span>
                  {t('dashboard:qrOrdering.meta.updated', {
                    defaultValue: 'Updated {{time}}',
                    time: lastUpdatedLabel,
                  })}
                </span>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                {t('dashboard:qrOrdering.meta.never', { defaultValue: 'Status not updated yet' })}
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t(`dashboard:qrOrdering.helper.${status}`, {
              defaultValue:
                status === 'active'
                  ? 'Guests scanning QR codes can place orders right away.'
                  : status === 'paused'
                  ? 'Guests see a pause banner and are guided to the counter.'
                  : 'Guests see that QR ordering is offline and are directed to staff.',
            })}
          </p>
          {status !== 'active' && currentGuestCopy ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200/60 bg-amber-50/70 p-3 text-sm text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">
                  {t('dashboard:qrOrdering.preview.heading', { defaultValue: 'Guest warning preview' })}
                </p>
                <p>{currentGuestCopy}</p>
                {currentResumeCopy ? <p className="text-xs opacity-80">{currentResumeCopy}</p> : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          {ACTIONS.map(({ status: targetStatus, icon: ActionIcon, theme, requiresDuration }) => {
            const isActive = status === targetStatus;
            const baseClass =
              theme === 'positive'
                ? 'border-emerald-500/40 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-300 dark:hover:bg-emerald-500/10'
                : theme === 'warning'
                ? 'border-amber-500/40 text-amber-600 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-300 dark:hover:bg-amber-500/10'
                : 'border-destructive/50 text-destructive hover:bg-destructive/10 dark:border-destructive/40 dark:text-destructive dark:hover:bg-destructive/20';
            const activeClass =
              theme === 'positive'
                ? 'bg-emerald-600 text-white hover:bg-emerald-600'
                : theme === 'warning'
                ? 'bg-amber-500 text-amber-950 hover:bg-amber-500'
                : 'bg-destructive text-destructive-foreground hover:bg-destructive';
            return (
              <Button
                key={targetStatus}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-full border',
                  isActive ? activeClass : baseClass,
                )}
                disabled={mutation.isPending || isActive || !selectedLocation}
                onClick={() => handleActionClick(targetStatus, Boolean(requiresDuration))}
              >
                <ActionIcon className="h-4 w-4" />
                {t(`dashboard:qrOrdering.buttons.${targetStatus}`, {
                  defaultValue:
                    targetStatus === 'active'
                      ? 'Resume'
                      : targetStatus === 'paused'
                      ? 'Pause'
                      : 'Stop',
                })}
              </Button>
            );
          })}
        </div>
      </CardContent>
      </Card>

      <AlertDialog
      open={dialogOpen}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) {
          setDialogOpen(false);
          setPendingStatus(null);
        } else if (!mutation.isPending) {
          setDialogOpen(open);
        }
      }}
      >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {pendingStatus
              ? t(`dashboard:qrOrdering.dialog.title.${pendingStatus}`, {
                  defaultValue: pendingStatus === 'paused' ? 'Pause QR ordering' : 'Stop QR ordering',
                })
              : t('dashboard:qrOrdering.dialog.title.paused', { defaultValue: 'Pause QR ordering' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pendingStatus
              ? t(`dashboard:qrOrdering.dialog.description.${pendingStatus}`, {
                  defaultValue:
                    pendingStatus === 'paused'
                      ? 'Guests scanning QR codes will see a pause warning.'
                      : 'Guests scanning QR codes will see that QR ordering is offline.',
                })
              : null}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm font-medium text-foreground">
            {t('dashboard:qrOrdering.duration.label', { defaultValue: 'Show this warning for' })}
          </p>
          <div className="space-y-2">
            {durationOptions.map((option) => {
              const isSelected = pendingDuration === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => setPendingDuration(option.value)}
                  className={cn(
                    'w-full rounded-xl border p-3 text-left transition-colors',
                    isSelected
                      ? 'border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-400 dark:bg-amber-500/10 dark:text-amber-50'
                      : 'border-border hover:border-amber-300',
                    mutation.isPending ? 'opacity-60' : '',
                  )}
                >
                  <p className="text-sm font-semibold">{option.label}</p>
                  <p className="text-xs text-muted-foreground">{option.helper}</p>
                </button>
              );
            })}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={mutation.isPending}
            onClick={() => {
              if (mutation.isPending) {
                return;
              }
              setDialogOpen(false);
              setPendingStatus(null);
            }}
          >
            {t('common:actions.cancel', { defaultValue: 'Cancel' })}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={mutation.isPending || !pendingStatus}
            onClick={(event) => {
              event.preventDefault();
              if (!pendingStatus) {
                return;
              }
              submitStatusChange(pendingStatus, pendingDuration);
            }}
          >
            {t('common:actions.save', { defaultValue: 'Save changes' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
